import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { listBlog, mediaUrl } from "@/lib/api";
import Seo from "@/components/Seo";
import NewsletterSignup from "@/components/NewsletterSignup";
import { breadcrumbSchema } from "@/lib/schemas";

const EASE = [0.76, 0, 0.24, 1];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export default function InsightsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listBlog()
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <Seo
        title="Insights — Essays on HR, hiring & fractional HR work"
        description="Practical essays for founders and boards on HR strategy, compensation, employer branding and fractional HR engagements."
        path="/insights"
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
          ]),
        ]}
      />

      <section className="pt-40 pb-16 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="overline mb-6">Insights · Essays</p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.92] text-[var(--ink)]"
            >
              Notes from the{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                practice
              </span>
              .
            </motion.h1>
          </div>
          <div className="md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              className="text-[var(--ink-80)] text-lg leading-relaxed max-w-2xl"
            >
              Short, practical essays for founders and boards — decisions we&apos;ve
              seen compound and mistakes we&apos;ve seen cost. No listicles, no thought-leadership fog.
            </motion.p>
          </div>
        </div>
      </section>

      <NewsletterSignup />

      <section className="py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          {loading && <p className="text-[var(--ink-50)]">Loading…</p>}
          {!loading && posts.length === 0 && (
            <p className="text-[var(--ink-50)]">No posts yet.</p>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: 0.05 * i, ease: EASE }}
                className="group border border-[var(--line-strong)] bg-[var(--bg)] hover:bg-[var(--bg-2)] transition-colors"
                data-testid={`insight-card-${p.slug}`}
              >
                <Link to={`/insights/${p.slug}`}>
                  {p.cover_image && (
                    <div className="clip-frame aspect-[4/3]">
                      <img src={mediaUrl(p.cover_image)} alt={p.title} />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {(p.tags || []).slice(0, 2).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] border border-[var(--accent)]/40 px-2 py-1"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <h2 className="font-display text-xl md:text-2xl tracking-[-0.01em] text-[var(--ink)] leading-tight mb-3">
                      {p.title}
                    </h2>
                    <p className="text-sm text-[var(--ink-70)] leading-relaxed line-clamp-3">
                      {p.excerpt}
                    </p>
                    <div className="mt-6 flex items-center justify-between">
                      <span className="text-xs uppercase tracking-[0.22em] text-[var(--ink-50)]">
                        {formatDate(p.published_at)}
                      </span>
                      <ArrowUpRight
                        className="w-4 h-4 text-[var(--ink)] group-hover:text-[var(--accent)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                        strokeWidth={1.6}
                      />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
